// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';

import { VaccineOutputs } from '../../lib/models';

interface ScenarioWaterfallProps {
  model: VaccineOutputs;
  title?: string;
}

/**
 * Scenario Waterfall showing the "budget story" of programme costs vs savings
 * Each component (delivery cost, treatment savings, productivity savings, AE costs) 
 * is shown as a sequential bar leading to net budget impact
 * 
 * Values are calculated from the same source as KPIs to ensure consistency:
 * - Treatment savings = noVaccinate.healthcareCost - vaccinate.healthcareCost
 * - Productivity savings = noVaccinate.indirectCost - vaccinate.indirectCost
 * - Programme cost = vaccinate.programmeCost (includes AE, need to separate)
 * - Net budget impact = model.budgetImpact (from KPI calculation)
 */
export const ScenarioWaterfall: React.FC<ScenarioWaterfallProps> = ({
  model,
  title = 'Budget impact waterfall',
}) => {
  const [graphDiv, setGraphDiv] = useState<any | null>(null);

  // Calculate values from the same source as KPIs
  const treatmentSavings = model.noVaccinate.healthcareCost - model.vaccinate.healthcareCost;
  const productivitySavings = model.noVaccinate.indirectCost - model.vaccinate.indirectCost;
  
  // Programme cost includes adverse events, need to separate
  // Use costBreakdown which has them separated
  const programmeCost = model.costBreakdown.programmeDeliveryCost;
  const aeCost = model.costBreakdown.adverseEventCost;
  
  // Use budget impact from KPI calculation to ensure consistency
  const netBudgetImpact = model.budgetImpact;

  // Detect scale: find the maximum absolute value to determine appropriate unit
  const allValues = [
    programmeCost,
    treatmentSavings,
    productivitySavings,
    aeCost,
    Math.abs(netBudgetImpact),
  ];
  const maxAbsValue = Math.max(...allValues, 1); // At least 1 to avoid division by zero

  // Determine scale: millions (>1M), thousands (>1k), or raw (<1k)
  let divisor: number;
  let suffix: string;
  let yAxisUnit: string;
  
  if (maxAbsValue >= 1_000_000) {
    divisor = 1_000_000;
    suffix = ' M';
    yAxisUnit = 'million';
  } else if (maxAbsValue >= 1_000) {
    divisor = 1_000;
    suffix = ' k';
    yAxisUnit = 'thousand';
  } else {
    divisor = 1;
    suffix = '';
    yAxisUnit = '';
  }

  // Format numbers for Finland locale: round to whole numbers, appropriate suffix
  const formatValue = (value: number, isNegative: boolean = false): string => {
    const absValue = Math.abs(value);
    if (absValue < 0.05 * divisor) return '0'; // Hide or show 0 for near-zero values
    
    const rounded = Math.round(absValue / divisor);
    const sign = isNegative ? '–' : '';
    return `${sign}€${rounded.toLocaleString('fi-FI')}${suffix}`;
  };

  // Calculate cumulative positions for waterfall (needed for y-axis range)
  // Waterfall starts at 0, then accumulates: +programmeCost, -treatmentSavings, -productivitySavings, +aeCost, final = netBudgetImpact
  const yValues = [
    0, // Starting position
    programmeCost / divisor,
    (programmeCost - treatmentSavings) / divisor,
    (programmeCost - treatmentSavings - productivitySavings) / divisor,
    (programmeCost - treatmentSavings - productivitySavings + aeCost) / divisor,
    netBudgetImpact / divisor, // Final position (should equal the previous if calculation is correct)
  ];
  
  // Calculate y-axis range with padding for outside text labels
  const yMin = Math.min(...yValues);
  const yMax = Math.max(...yValues);
  const yRange = yMax - yMin;
  const minPadding = divisor >= 1_000_000 ? 0.5 : divisor >= 1_000 ? 0.5 : 5; // Appropriate padding based on scale
  const padding = Math.max(yRange * 0.2, Math.abs(yMax) * 0.15, Math.abs(yMin) * 0.15, minPadding);
  const yAxisMin = yMin - padding;
  const yAxisMax = yMax + padding;

  // Reposition zoom hint near modebar when it appears
  useEffect(() => {
    if (!graphDiv) return;

    const repositionZoomHint = () => {
      // Find the zoom hint element (Plotly creates it dynamically)
      const plotContainer = graphDiv.parentElement;
      if (!plotContainer) return;

      // Look for the zoom hint div (usually contains "Double-click" text)
      const zoomHint = Array.from(plotContainer.querySelectorAll('div')).find(
        (div: any) => div.textContent?.includes('Double-click') || div.textContent?.includes('zoom')
      ) as HTMLElement | undefined;

      if (zoomHint) {
        const modebar = plotContainer.querySelector('.modebar');
        if (modebar) {
          const modebarRect = modebar.getBoundingClientRect();
          
          // Position hint near modebar (top-right, below modebar)
          zoomHint.style.position = 'absolute';
          zoomHint.style.top = `${modebarRect.height + 10}px`;
          zoomHint.style.right = '10px';
          zoomHint.style.left = 'auto';
          zoomHint.style.bottom = 'auto';
          zoomHint.style.backgroundColor = 'rgba(15, 23, 42, 0.9)';
          zoomHint.style.color = '#e2e8f0';
          zoomHint.style.padding = '8px 12px';
          zoomHint.style.borderRadius = '6px';
          zoomHint.style.border = '1px solid #334155';
          zoomHint.style.fontFamily = "'Inter', sans-serif";
          zoomHint.style.fontSize = '12px';
          zoomHint.style.zIndex = '1000';
          zoomHint.style.pointerEvents = 'none';
        }
      }
    };

    // Check periodically for zoom hint (Plotly creates it after zoom)
    const interval = setInterval(repositionZoomHint, 500);

    return () => clearInterval(interval);
  }, [graphDiv]);

  return (
    <div className="vaccine-demo__chart-container">
      <div className="vaccine-demo__chart-header">
        <div className="vaccine-demo__chart-title">{title}</div>
        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px', fontStyle: 'italic' }}>
          Negative values = savings
        </div>
      </div>
      <div className="vaccine-demo__chart" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Plot
          onInitialized={(_, gd) => setGraphDiv(gd)}
          onUpdate={(_, gd) => setGraphDiv(gd)}
          data={[
            {
              type: 'waterfall',
              orientation: 'v',
              x: [
                'Programme<br>Delivery',
                'Treatment<br>Savings',
                'Productivity<br>Savings',
                'Adverse<br>Events',
                'Net Budget<br>Impact',
              ],
              y: [
                programmeCost / divisor,
                -treatmentSavings / divisor,
                -productivitySavings / divisor,
                aeCost / divisor,
                netBudgetImpact / divisor,
              ],
              measure: ['relative', 'relative', 'relative', 'relative', 'total'],
              text: [
                formatValue(programmeCost),
                formatValue(treatmentSavings, true),
                formatValue(productivitySavings, true),
                formatValue(aeCost),
                formatValue(netBudgetImpact, netBudgetImpact < 0),
              ],
              textposition: 'outside',
              textfont: { 
                color: '#e2e8f0', 
                size: 14, 
                family: 'Inter, sans-serif' 
              },
              hovertemplate: [
                'Programme Delivery<br>%{text}<extra></extra>',
                'Treatment Savings<br>%{text}<extra></extra>',
                'Productivity Savings<br>%{text}<extra></extra>',
                'Adverse Events<br>%{text}<extra></extra>',
                'Net Budget Impact<br>%{text}<extra></extra>',
              ],
              connector: {
                line: { color: 'rgba(148, 163, 184, 0.4)', width: 1.5 }, // Lightened connectors for subtlety
              },
              increasing: { marker: { color: '#f87171' } }, // Costs in lighter red for better contrast
              decreasing: { marker: { color: '#34d399' } }, // Savings in lighter green for better contrast
              totals: { marker: { color: '#3b82f6' } }, // Distinct blue for Net bar to stand out
            } as any, // Type assertion needed for waterfall-specific properties
          ]}
          layout={{
            autosize: true,
            margin: { l: 80, r: 100, t: 50, b: 140 }, // Increased bottom margin to lower x-axis title
            xaxis: {
              title: { 
                text: 'Component of budget impact', 
                font: { color: '#e2e8f0', size: 14, family: 'Inter, sans-serif' },
                standoff: 10, // Additional space between title and axis
              },
              tickfont: { size: 13, color: '#e2e8f0', family: 'Inter, sans-serif' },
            },
            yaxis: {
              title: { 
                text: yAxisUnit ? `Budget impact (€ ${yAxisUnit})` : 'Budget impact (€)', 
                font: { color: '#e2e8f0', size: 14, family: 'Inter, sans-serif' } 
              },
              tickformat: ',.0f', // Round to whole numbers (no decimals)
              tickfont: { color: '#e2e8f0', size: 13, family: 'Inter, sans-serif' },
              gridcolor: '#1f2937',
              range: [yAxisMin, yAxisMax], // Explicit range with padding for outside text labels
            },
            paper_bgcolor: 'rgba(0,0,0,0)',
            plot_bgcolor: 'rgba(0,0,0,0)',
            font: { color: '#e2e8f0', family: 'Inter, sans-serif' },
            showlegend: false,
            hoverlabel: {
              bgcolor: 'rgba(15, 23, 42, 0.95)',
              bordercolor: '#334155',
              font: {
                color: '#e2e8f0',
                family: 'Inter, sans-serif',
                size: 13,
              },
            },
          }}
          config={{ 
            responsive: true, 
            displaylogo: false, 
            displayModeBar: true,
            modeBarButtonsToRemove: [
              'pan2d', 
              'lasso2d', 
              'select2d',
              'autoScale2d',
              'hoverClosestCartesian',
              'hoverCompareCartesian',
              'toggleSpikelines',
            ],
            doubleClick: 'reset',
          }}
          style={{ width: '100%', height: '100%', minHeight: '400px' }}
          useResizeHandler
        />
      </div>
    </div>
  );
};

export default ScenarioWaterfall;
